import { ArrowRight, Plus, Store } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { KanameGlyph } from '@/components/brand/KanameGlyph';
import { useObserverAnchor } from '@/components/observer/useObserverAnchor';
import { plural } from '@/components/observer/observerEvents';
import { Badge, Button, EmptyState, EntityChip, PageHeader, cn } from '@/components/ui';
import { ObjectLink } from '@/components/layout/workspace/ObjectLink';
import { useWorkspace } from '@/components/layout/workspace/workspaceContext';
import {
  SAMPLE_FACTS,
  at,
  kindLabel,
  myServices,
  servicesLabel,
  withServices,
  type MyService,
} from '../model';
import { usePrefs } from '../prefs';
import { useShellCommands } from '../shellCommands';
import { Body, Page, Panel } from './common';
import { ModuleIcon, ModuleStatus } from './MarketplaceScreen';

/*
  Главная: меню раскрыто с подписями, второго уровня нет. Отсюда — в свои сервисы и к тому, что
  требует внимания; объекты открываются вкладками. Без сервисов — пустой центр «Центр без сервисов —
  ещё не Kaname» (spec-1 §3.1): один призыв — в Marketplace. Сервисы — строками в порядке подключения,
  это и порядок пластин веера в «Моих сервисах»; новые приезжают (R35).
*/

const ROW =
  'group flex w-full items-center gap-4 rounded-md border border-line bg-canvas p-4 text-left transition-interactive hover:border-line-strong hover:bg-hover';

/** Строка INC-2189 — цель помощника на «Нужен ответ» (sampleEvents.ts, ASK_ANCHORS). */
const INCIDENT_ANCHOR = { role: 'item', label: 'INC-2189 · Load average выше 8 на host-338' } as const;

export function HomeScreen() {
  const { go } = useWorkspace();
  const prefs = usePrefs();
  const list = myServices(prefs);
  const n = list.length;
  const canConnect = withServices(prefs.services, prefs.servicesOff).some((m) => !m.installed && !m.soon);
  // «Требует внимания» — от включённой «Инфраструктуры»: выключенный сервис событий не шлёт.
  const infraOn = list.some((s) => s.module.id === 'infra' && s.on);

  // Новые строки приезжают: свежие id считаются при смене состава и держатся до следующей смены,
  // чтобы класс не снялся посреди анимации. «Показано» записывается сразу.
  const { services, seenServices, set } = prefs;
  const key = services.join('|');
  const fresh = () => services.filter((id) => !seenServices.includes(id));
  const [arrived, setArrived] = useState(() => ({ key, ids: fresh() }));
  if (arrived.key !== key) setArrived({ key, ids: fresh() });
  useEffect(() => {
    if (services.length !== seenServices.length || services.some((id, i) => id !== seenServices[i])) {
      set({ seenServices: services });
    }
  }, [services, seenServices, set]);

  // Счётчик — у оболочки: «Пришло уведомление» на верхней полосе его увеличивает.
  const { unread: count } = useShellCommands();
  const unread = (
    <span>
      {count}{' '}
      {plural(count, ['непрочитанное уведомление', 'непрочитанных уведомления', 'непрочитанных уведомлений'])}
    </span>
  );
  const incidentRef = useObserverAnchor<HTMLLIElement>('obj.INC-2189', INCIDENT_ANCHOR);

  if (n === 0)
    return (
      <Page>
        <PageHeader
          title="Добрый вечер"
          meta={
            <>
              <span>{servicesLabel(0)}</span>
              {unread}
            </>
          }
        />
        <Body>
          <EmptyState
            icon={<KanameGlyph petals={0} seats core="ring" />}
            title="Центр без сервисов — ещё не Kaname"
            description="Каждый подключённый сервис становится пластиной веера в «Моих сервисах»."
            action={{ label: 'Открыть Marketplace', onClick: () => go(at('/marketplace/recommended')) }}
          />
        </Body>
      </Page>
    );

  return (
    <Page>
      <PageHeader
        title="Добрый вечер"
        meta={
          <>
            <span>{servicesLabel(n)}</span>
            {unread}
          </>
        }
        actions={
          <Button variant="secondary" leftIcon={<Store />} onClick={() => go(at('/marketplace'))}>
            Marketplace
          </Button>
        }
      />
      <Body>
        <div className="grid gap-4 lg:grid-cols-2">
          <Panel
            title="Мои сервисы"
            aside={
              <Button
                variant="link"
                size="sm"
                rightIcon={<ArrowRight />}
                onClick={() => go(at('/marketplace/installed'))}
              >
                Веер
              </Button>
            }
            className={cn(!infraOn && 'lg:col-span-2')}
          >
            <div className={cn('gap-2', infraOn ? 'flex flex-col' : 'grid md:grid-cols-2')}>
              {list.map((s) => (
                <ServiceRow key={s.module.id} service={s} arrived={arrived.ids.includes(s.module.id)} />
              ))}
              {canConnect && <ConnectRow />}
            </div>
          </Panel>
          {infraOn && (
            <Panel title="Требует внимания" className="self-start">
              <ul className="flex flex-col divide-y divide-line">
                <li ref={incidentRef} className="flex items-center gap-3 py-2">
                  <EntityChip kind="incident" name="INC-2189" size="sm" />
                  <ObjectLink
                    to={at('/apps/infra/incident/INC-2189')}
                    className="flex-1 truncate text-sm text-fg hover:underline"
                  >
                    Load average выше 8 на host-338
                  </ObjectLink>
                  <span className="text-xs text-fg-subtle tabular-nums">34 мин</span>
                </li>
                <li className="flex items-center gap-3 py-2">
                  <EntityChip kind="host" name="host-338" size="sm" />
                  <ObjectLink
                    to={at('/apps/infra/node/host-338')}
                    className="flex-1 truncate text-sm text-fg hover:underline"
                  >
                    Узел с двумя проблемами за сутки
                  </ObjectLink>
                  <span className="text-xs text-fg-subtle tabular-nums">2 ч</span>
                </li>
              </ul>
            </Panel>
          )}
        </div>
      </Body>
    </Page>
  );
}

/** Строка сервиса: включённая «Инфраструктура» открывает приложение, остальные — карточку модуля вкладкой. */
function ServiceRow({ service: s, arrived }: { service: MyService; arrived: boolean }) {
  const { go } = useWorkspace();
  const m = s.module;
  const app = m.id === 'infra' && s.on;
  const className = cn(ROW, arrived && 'animate-arrive motion-reduce:animate-none');
  const body = (
    <RowBody
      icon={<ModuleIcon id={m.id} className="size-5" />}
      title={m.name}
      sub={(s.on && SAMPLE_FACTS[m.id]) || kindLabel(m)}
      status={
        app ? (
          <Badge variant="urgent" solid>
            2 High
          </Badge>
        ) : (
          <ModuleStatus module={m} alert={!!s.alert} />
        )
      }
    />
  );
  return app ? (
    <button type="button" onClick={() => go(at('/apps/infra'))} className={className}>
      {body}
    </button>
  ) : (
    <ObjectLink to={at(`/marketplace/m/${m.id}`)} className={className}>
      {body}
    </ObjectLink>
  );
}

/** Последняя строка, пока в каталоге есть что подключить: той же высоты, что строка сервиса (R31). */
function ConnectRow() {
  const { go } = useWorkspace();
  return (
    <button
      type="button"
      onClick={() => go(at('/marketplace/recommended'))}
      className={cn(ROW, 'border-dashed')}
    >
      <RowBody
        icon={<Plus className="size-5 text-fg-muted" />}
        dashed
        title="Подключить сервис"
        sub="Новый сервис станет пластиной веера"
      />
    </button>
  );
}

function RowBody({
  icon,
  title,
  sub,
  status,
  dashed = false,
}: {
  icon: ReactNode;
  title: string;
  sub: string;
  status?: ReactNode;
  dashed?: boolean;
}) {
  return (
    <>
      <span
        className={cn(
          'grid size-10 shrink-0 place-items-center rounded-md border border-line text-fg',
          dashed ? 'border-dashed' : 'bg-muted',
        )}
      >
        {icon}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-semibold text-fg">{title}</span>
        <span className="truncate text-xs text-fg-muted">{sub}</span>
      </span>
      {status}
      <ArrowRight className="size-4 shrink-0 text-fg-subtle transition-transform duration-fast group-hover:translate-x-0.5" />
    </>
  );
}
